import { useTheme } from "@emotion/react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ErrorMSG from "../../components/ErrorMSG";
import MyDialog from "../../components/MyDialog";
import { passwordRegex } from "../../utils/regex";
import { addConfirmPassword } from "./redux/action";
import { HidePasswordIcon, ShowPasswordIcon } from "../../components/Icons";

export default function ChangePassword({ openDialog, handleClose }) {
  const dispatch = useDispatch();
  const { palette, breakpoints } = useTheme();
  const isSmall = useMediaQuery(breakpoints.down("sm") || breakpoints.down("xs"));
  const gridSpacing = isSmall ? 2 : 3;
  const [showPrevPassword, setShowPrevPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);

  // main state to store the payload
  const {
    handleSubmit,
    register,
    control,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      previousPassword: "",
      password: "",
      confirmPassword: ""
    }
  });

  const onSubmit = async (data) => {
    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      await dispatch(
        addConfirmPassword({ previousPassword: data?.previousPassword, password: data.password }, user?.userType)
      ).then(async (data) => {
        setIsLoading(false);
        if (data && data?.payload?.status) {
          toast.success(data.payload.message);
          reset({ previousPassword: "", password: "", confirmPassword: "" });
          handleClose();
        }
      });
    }
  };

  useEffect(() => {
    if (!openDialog) {
      reset({ previousPassword: "", password: "", confirmPassword: "" });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog]);

  return (
    <>
      <MyDialog
        maxWidth="sm"
        open={openDialog}
        handleClose={() => {
          handleClose();
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <DialogTitle className="modal-header">CHANGE PASSWORD</DialogTitle>
          <DialogContent>
            <Box m="10px">
              <Grid container spacing={gridSpacing}>
                <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      type={showPrevPassword ? "text" : "password"}
                      size="small"
                      label={
                        <p className="my-0">
                          Current Password <span className="danger mr-0">*</span>
                        </p>
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              className="disabledPassEyeIcon"
                              aria-label="toggle password visibility"
                              onClick={() => setShowPrevPassword(!showPrevPassword)}
                              edge="end"
                              disabled={watch().previousPassword.length === 0}
                            >
                              {showPrevPassword ? (
                                <HidePasswordIcon width="20px" height="20px" color={palette.primary.main} />
                              ) : (
                                <ShowPasswordIcon width="20px" height="20px" color={palette.primary.main} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      {...register("previousPassword", { required: true, pattern: passwordRegex })}
                    />
                    {errors.password && errors.password.type === "required" && (
                      <ErrorMSG text="Current Password is required" />
                    )}
                    {errors.password && errors.password.type === "pattern" && (
                      <ErrorMSG text="Password at least 1 uppercase, 1 lowercase, 1 special character, 1 number, Min 8, Max 30" />
                    )}
                  </FormControl>
                </Grid>
                <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      size="small"
                      label={
                        <p className="my-0">
                          New Password <span className="danger mr-0">*</span>
                        </p>
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              className="disabledPassEyeIcon"
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={watch().password.length === 0}
                            >
                              {showPassword ? (
                                <HidePasswordIcon width="20px" height="20px" color={palette.primary.main} />
                              ) : (
                                <ShowPasswordIcon width="20px" height="20px" color={palette.primary.main} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      {...register("password", { required: true, pattern: passwordRegex })}
                    />
                    {errors.password && errors.password.type === "required" && (
                      <ErrorMSG text="New Password is required" />
                    )}
                    {errors.password && errors.password.type === "pattern" && (
                      <ErrorMSG text="Password at least 1 uppercase, 1 lowercase, 1 special character, 1 number, Min 8, Max 30" />
                    )}
                  </FormControl>
                </Grid>
                <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      size="small"
                      label={
                        <p className="my-0">
                          Confirm New Password <span className="danger mr-0">*</span>
                        </p>
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              className="disabledPassEyeIcon"
                              aria-label="toggle password visibility"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              disabled={watch().confirmPassword.length === 0}
                            >
                              {showConfirmPassword ? (
                                <HidePasswordIcon width="20px" height="20px" color={palette.primary.main} />
                              ) : (
                                <ShowPasswordIcon width="20px" height="20px" color={palette.primary.main} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      {...register("confirmPassword", {
                        required: true,
                        validate: (val) => control._formValues.password === val
                      })}
                    />
                    {errors.confirmPassword && errors.confirmPassword.type === "required" && (
                      <ErrorMSG text="Confirm New Password is required" />
                    )}
                    {errors.confirmPassword && errors.confirmPassword.type === "validate" && (
                      <ErrorMSG text="Your Confirm password does not match" />
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              disabled={isLoading}
              onClick={() => {
                handleClose();
              }}
            >
              {isLoading ? "Wait..." : "Close"}
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading ? "Wait..." : "Save"}
            </Button>
          </DialogActions>
        </form>
      </MyDialog>
    </>
  );
}
